import React, {useEffect} from 'react';
import {Info} from "lucide-react";
import {Link, Outlet, useLocation, useNavigate} from "react-router-dom";
import AuthContext, {useAuth} from "./contexts/AuthContext";
// noinspection ES6PreferShortImport
import {DISCORD_INVITE} from "./pages/Index";
import Toaster from "./components/Toaster";
import PaddleContext from "./contexts/PaddleContext";
import AccountMenu from "./account/AccountMenu";

export default function Home() {
    return <AuthContext>
        <PaddleContext>
            <HomeImpl />
        </PaddleContext>
    </AuthContext>
}

function HomeImpl() {
    const auth = useAuth()
    const location = useLocation()
    const navigate = useNavigate()

    const isLoggedIn = auth.isLoggedIn ?? false

    // Scroll to a section when the URL carries a hash (e.g. arriving at "/#features" from another page).
    useEffect(() => {
        if (!location.hash) return
        document.getElementById(location.hash.slice(1))?.scrollIntoView({behavior: "smooth"})
    }, [location])

    const scrollToFeatures = () => {
        if (location.pathname === "/") {
            document.getElementById("features")?.scrollIntoView({behavior: "smooth"})
        } else {
            navigate("/#features")
        }
    }

    return <>
        <div id="home-root" className="bg-neutral-900 text-white flex flex-col min-h-screen h-full backdrop-blur-md backdrop-brightness-50">

            {/* Header */}
            <header className="sticky top-0 z-50 mt-4 md:mt-6 max-w-6xl w-full self-center px-4 md:px-6">
                <div className="rounded-2xl border border-neutral-800 bg-neutral-900/80 backdrop-blur-lg shadow-lg shadow-black/20 px-3 md:px-5 py-2.5 flex items-center justify-between">
                    {/* Logo */}
                    <Link className="flex items-center" to="/">
                        <img
                            src="/android-chrome-512x512.png"
                            alt="Logo"
                            className="size-8 rounded-md"
                        />
                        <span className="ms-3 text-lg md:text-xl font-semibold whitespace-nowrap text-white">
                            Rust Server Tool
                        </span>
                        <span className="ms-3 hidden sm:inline-block bg-primary/15 text-primary border border-primary/30 rounded-md px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold">
                            Beta
                        </span>
                    </Link>

                    {/* Nav + auth */}
                    <div className="flex items-center gap-1 md:gap-3">
                        {isLoggedIn ? <AccountMenu/> : (
                            <>
                                <button onClick={scrollToFeatures}
                                        className="hidden sm:inline-flex text-neutral-300 hover:text-white font-medium px-3 py-2 rounded-lg transition-colors">
                                    Features
                                </button>
                                <Link to="/pricing"
                                      className="hidden sm:inline-flex text-neutral-300 hover:text-white font-medium px-3 py-2 rounded-lg transition-colors">
                                    Pricing
                                </Link>
                                <Link to="/login"
                                      className="inline-flex justify-center items-center gap-2 bg-gradient-to-r from-primary to-sky-400 hover:from-primary-hover hover:to-sky-300 text-white px-4 py-2 rounded-lg font-semibold shadow-lg shadow-primary/20 transition-all">
                                    Sign In
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            <div className="flex flex-col flex-1 overflow-auto rst-scrollbar pt-2">
                <main className="flex-grow flex flex-col">

                    <Toaster />

                    <div className="max-w-4xl w-full mt-4 mx-auto px-6">
                        <div className="flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/10 p-4 backdrop-blur-lg">
                            <div className="shrink-0 inline-flex items-center justify-center size-9 rounded-xl bg-primary/15 text-primary border border-primary/30">
                                <Info className="size-5"/>
                            </div>
                            <div className="text-sm text-neutral-300 leading-relaxed">
                                <h3 className="text-white font-semibold mb-0.5">Important Notice</h3>
                                <p>The site is currently in Beta and still under heavy development.
                                    Features might change or get removed at any time without prior notice.
                                    Join the <a href={DISCORD_INVITE} target="_blank" rel="noreferrer" className="text-primary hover:underline font-medium">Discord</a> to
                                    get all the latest news and updates.</p>
                            </div>
                        </div>
                    </div>

                    <Outlet/>

                </main>

                {/* Footer */}
                <footer className="bg-neutral-800 py-6 text-center z-50 text-neutral-400 space-y-3">
                    <p>Rust Server Tool is not affiliated with Facepunch Studios or Steam. All trademarks referenced are the properties of their respective owners.</p>
                    <p>&copy; 2026 Rust Server Tool. All rights reserved.</p>
                    <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
                        <Link to="/imprint" className="hover:text-white transition-colors">Imprint</Link>
                        <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
                    </nav>
                </footer>
            </div>


        </div>

    </>
}
